import { useRef, useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { api, BACKEND } from "@/lib/api";

const stockShots = [
  { url: "https://images.unsplash.com/photo-1625690180114-5530b1304127", label: "Auto Interior", span: "lg:col-span-7 lg:row-span-2 h-[420px] lg:h-full" },
  { url: "https://images.pexels.com/photos/13722886/pexels-photo-13722886.jpeg", label: "Home Interior", span: "lg:col-span-5 h-[300px]" },
  { url: "https://images.pexels.com/photos/14231678/pexels-photo-14231678.jpeg", label: "The Detail", span: "lg:col-span-5 h-[300px]" },
];

const gridSpans = [
  "lg:col-span-7 lg:row-span-2 h-[420px] lg:h-full",
  "lg:col-span-5 h-[300px]",
  "lg:col-span-5 h-[300px]",
  "lg:col-span-4 h-[280px]",
  "lg:col-span-4 h-[280px]",
  "lg:col-span-4 h-[280px]",
];

function Shot({ s }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 1 }}
      ref={ref}
      className={`relative overflow-hidden clip-frame group ${s.span}`}
      data-testid={`gallery-shot-${(s.label || 'photo').toLowerCase().replace(/\s/g, "-")}`}
    >
      <motion.img
        style={{ y }}
        src={s.url}
        alt={s.label || "Detail"}
        className="absolute inset-0 w-full h-[125%] object-cover -top-[12%] group-hover:scale-105 transition-transform duration-[1.2s]"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
      {s.label && (
        <div className="absolute bottom-6 left-6 z-10">
          <span className="text-xs tracking-[0.25em] uppercase text-primary">{s.label}</span>
        </div>
      )}
    </motion.div>
  );
}

export default function Gallery() {
  const [shots, setShots] = useState(stockShots);

  useEffect(() => {
    api.get("/gallery").then((r) => {
      const items = r.data || [];
      if (items.length > 0) {
        setShots(items.slice(0, 6).map((it, i) => ({
          url: `${BACKEND}${it.url}`,
          label: null,
          span: gridSpans[i] || "lg:col-span-4 h-[280px]",
        })));
      }
    }).catch(() => {});
  }, []);

  return (
    <section id="work" className="py-24 lg:py-32 max-w-[1400px] mx-auto px-6 lg:px-10" data-testid="gallery-section">
      <div className="flex items-end justify-between mb-14">
        <h2 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-none text-foreground">
          Selected<br /><span className="italic text-primary">work.</span>
        </h2>
        <p className="hidden sm:block text-muted-foreground font-light max-w-xs text-right">
          A glimpse of the finish we deliver, in vehicles and homes across the Gallatin Valley.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6 lg:h-[640px]">
        {shots.map((s, i) => <Shot key={i} s={s} />)}
      </div>
    </section>
  );
}
